import { ReactElement } from 'react'
import { useFormContext } from 'react-hook-form'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button, Stack, Typography, CircularProgress, Box } from '@mui/material'
import type { OrganisationConstructionCompanyDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationConstructionCompaniesQueryKey,
  getGetProjectConstructionCompaniesQueryKey,
  useCreateOrganisationConstructionCompany,
  useCreateProjectConstructionCompany,
} from '@api/participants/participants'
import { useQueryClient } from '@tanstack/react-query'
import { Form2, TextField } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { constructionCompanySchema } from './schema'

interface AddConstructionCompanyFormProps {
  onClose: () => void
  // Present when editing a project: the company then joins the project's own
  // organisation's list, which is not necessarily the active one (R18).
  projectGuid?: string
}

interface ConstructionCompanyFormData {
  name: string
  street: string
  zip_code: string
  city: string
}

/**
 * Adds an Auftraggeberin to the acting organisation's own list (R18) and
 * selects it in the surrounding project form right away.
 */
const AddConstructionCompanyForm = ({
  onClose,
  projectGuid,
}: AddConstructionCompanyFormProps): ReactElement => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { setValue } = useFormContext()

  const handleSuccess = async (company: OrganisationConstructionCompanyDTO) => {
    await queryClient.invalidateQueries(
      projectGuid
        ? getGetProjectConstructionCompaniesQueryKey(projectGuid)
        : getGetOrganisationConstructionCompaniesQueryKey(),
    )
    setValue('construction_company_name', company.name)
    enqueueSnackbar('Auftraggeberin erstellt', { variant: 'success' })
    onClose()
  }

  const handleError = (error: AxiosError) => {
    enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erstellen der Auftraggeberin'), {
      variant: 'error',
    })
  }

  const { mutate: createOrganisationCompany, isLoading: isCreatingForOrganisation } =
    useCreateOrganisationConstructionCompany<AxiosError>({
      mutation: { onSuccess: handleSuccess, onError: handleError },
    })
  const { mutate: createProjectCompany, isLoading: isCreatingForProject } =
    useCreateProjectConstructionCompany<AxiosError>({
      mutation: { onSuccess: handleSuccess, onError: handleError },
    })
  const isLoading = isCreatingForOrganisation || isCreatingForProject

  const defaultValues: ConstructionCompanyFormData = {
    name: '',
    street: '',
    zip_code: '',
    city: '',
  }

  return (
    <Box sx={{ minWidth: 400, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Neue Auftraggeberin hinzufügen
      </Typography>

      <Form2
        onSubmit={(data: ConstructionCompanyFormData) =>
          projectGuid
            ? createProjectCompany({ projectGuid, data })
            : createOrganisationCompany({ data })
        }
        defaultValues={defaultValues}
        validationSchema={constructionCompanySchema}
      >
        <Stack spacing={2}>
          <TextField
            name="name"
            label="Firmenname"
            placeholder="Name der Baufirma"
            required
            data-cy="input-construction-company-name"
          />

          <TextField
            name="street"
            label="Straße"
            placeholder="Straße und Hausnummer"
            data-cy="input-construction-company-street"
          />

          <Stack direction="row" spacing={1}>
            <TextField
              name="zip_code"
              label="PLZ"
              placeholder="PLZ"
              data-cy="input-construction-company-zip"
            />
            <TextField
              name="city"
              label="Stadt"
              placeholder="Stadt"
              data-cy="input-construction-company-city"
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={isLoading}
              data-cy="submit-construction-company-form"
            >
              {isLoading ? <CircularProgress size={20} /> : 'Erstellen'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onClose}
              disabled={isLoading}
              data-cy="cancel-construction-company-form"
            >
              Abbrechen
            </Button>
          </Stack>
        </Stack>
      </Form2>
    </Box>
  )
}

export default AddConstructionCompanyForm
